/* =============================================================================
   VIEWS / ADMIN / DASHBOARD-DATA — pure Statistik + Selektoren
   =============================================================================
   S202 (REF-8) — aus dashboard.js extrahiert. Enthält NUR window-freie bzw.
   state-lesende Rechenlogik (keine DOM-Manipulation, keine Renderer):
     - getState (Storage-Zugriff, Single-Source für alle Dashboard-Module)
     - centsToEUR, isoDate, lastNDaysIso, computeTrend (pure Helper)
     - getOrderStats, getOrderHeatmap, getReservationTrend, getOrderTrend
     - getCockpitCounters
     - Rerender-Callback-Registry (setDashboardRerender/requestDashboardRerender)

   Reine Extraktion, NULL Verhaltensänderung. Als Leaf-Modul importiert dieses
   File KEIN anderes Dashboard-Modul (acyclischer Schnitt).
============================================================================ */

import { todayISO } from '../../utils/helpers.js';
import { isOrderActive, countOrdersByBucket } from '../../core/order-statuses.js';
import { orderCountChannel } from '../../core/order-sources.js';
import { isActiveReservation } from '../../core/capacity.js';
import { itemsFor, orderChannels } from '../../core/order-items-cache.js';

export function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { reservations: [], orders: [], settings: {} };
}

// -----------------------------------------------------------------------------
// Rerender-Callback-Registry (acyclischer Rück-Ruf, Muster S201 orders-schwund
// onLoaded). Die Sub-Module (lists Expand/Collapse, timeline SetNextView-Fallback)
// müssen ein volles renderDashboard() auslösen können, dürfen aber den
// Orchestrator NICHT importieren (Zyklus). Der Orchestrator registriert seine
// renderDashboard-Referenz beim Laden; die Sub-Module rufen sie über data auf.
// -----------------------------------------------------------------------------
let _rerenderDashboard = null;
export function setDashboardRerender(fn) {
  if (typeof fn === 'function') _rerenderDashboard = fn;
}
export function requestDashboardRerender() {
  if (typeof _rerenderDashboard === 'function') _rerenderDashboard();
}

// -----------------------------------------------------------------------------
// Order-KPIs — Hilfsfunktionen für das Dashboard
// -----------------------------------------------------------------------------

/** Cent → "12,50 €" (deutsch). */
export function centsToEUR(cents) {
  const v = (Number(cents) || 0) / 100;
  return v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/**
 * Stats für heute: Bestellungs-Anzahl, Brutto-Umsatz, aktive vs. Bediener-
 * Aktion-pflichtige Bestellungen.
 *
 * Phase 22 (Session 22 Nachschlag): Counter-Konsistenz. Bisher hatte das
 * Dashboard eine eigene `ORDER_OPEN_STATUSES`-Liste die alle non-finished
 * Status zählte — das ist „aktiv", nicht „offen aus Bediener-Sicht".
 * Jetzt liefert `getOrderStats` BEIDE Werte (`openCount = awaiting operator`
 * + `activeCount = alle non-finished`), und das Dashboard-Widget entscheidet
 * lokal welcher Wert sinnvoll ist.
 */
// S122 — 3-Wege-Kanal-Split: „Bestell." zählt NUR Walk-In + Takeaway. Liefer-
// Orders (service_type='delivery') und reservierungs-verknüpfte Orders
// (reservation_id gesetzt) bekommen eigene Buckets — Single-Source via
// orderCountChannel (order-sources.js). Status-Buckets pro Kanal über
// countOrdersByBucket (order-statuses.js Single-Source). Test-Hook (S122).
export function getOrderStats(today) {
  const state = getState();
  const orders = Array.isArray(state.orders) ? state.orders : [];
  const todays = orders.filter(o => o.pickupDate === today);

  const split = { order: [], delivery: [], reservation: [] };
  for (const o of todays) split[orderCountChannel(o)].push(o);

  const statsFor = (list) => {
    const productive = list.filter(o => o.status !== 'cancelled' && o.status !== 'no_show');
    const b = countOrdersByBucket(list);           // order-statuses Single-Source
    return {
      count:         productive.length,
      revenueGross:  productive.reduce((s, o) => s + (Number(o.totalGrossCents) || 0), 0),
      openCount:     b.awaitingOperator,  // Orders die auf Bediener warten (pending oder ready)
      pipelineCount: b.inPipeline,        // Orders im KDS/Bar in Arbeit (confirmed/preparing)
      readyCount:    b.ready,             // Orders bereit zum Ausgeben
      activeCount:   b.active             // alle non-finished Orders (inkl. Pipeline)
    };
  };

  return {
    order:       statsFor(split.order),       // Walk-In + Takeaway = „Bestell."
    delivery:    statsFor(split.delivery),    // „Lieferung"
    reservation: statsFor(split.reservation), // verknüpfte Orders (nur informativ; NICHT in Bestell.)
    total:       statsFor(todays),            // alle Kanäle (Heatmap-Header, Gesamt-Anzeigen)
    todayOrders: todays
  };
}

/** Heatmap der nächsten N Stunden in 15-Min-Slots. Jeder Slot zählt
 *  Bestellungen, deren pickup_time in [slotStart, slotStart+15min) liegt. */
export function getOrderHeatmap(hoursAhead) {
  const state = getState();
  const orders = Array.isArray(state.orders) ? state.orders : [];
  const today  = todayISO();
  const now = new Date();
  // Auf nächsten 15-Min-Slot runden
  const startMin = Math.floor((now.getHours() * 60 + now.getMinutes()) / 15) * 15;
  const slotsTotal = (hoursAhead || 4) * 4;
  const slots = [];
  for (let i = 0; i < slotsTotal; i++) {
    const m = startMin + i * 15;
    const hh = Math.floor(m / 60);
    if (hh >= 24) break;
    const mm = m % 60;
    const label = String(hh).padStart(2, '0') + ':' + String(mm).padStart(2, '0');
    slots.push({ label, hhmm: hh * 60 + mm, count: 0, revenueGross: 0 });
  }
  for (const o of orders) {
    if (o.pickupDate !== today) continue;
    if (!isOrderActive(o)) continue;
    const t = typeof o.pickupTime === 'string' ? o.pickupTime : '';
    const [h, mi] = t.split(':').map(x => parseInt(x, 10) || 0);
    const total = h * 60 + mi;
    const slot = slots.find(s => total >= s.hhmm && total < s.hhmm + 15);
    if (slot) {
      slot.count += 1;
      slot.revenueGross += Number(o.totalGrossCents) || 0;
    }
  }
  return slots;
}

/** ISO YYYY-MM-DD eines Datums-Objekts (lokal). */
export function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Liefert die ISO-Daten der letzten N Tage (heute inkl.), ältester zuerst. */
export function lastNDaysIso(n) {
  const out = [];
  const today = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    out.push(isoDate(d));
  }
  return out;
}

/** Reservierungs-Trend der letzten 7 Tage (Anzahl bestätigt/pending). */
export function getReservationTrend(days = 7) {
  const state = getState();
  const all = state.reservations || [];
  const dates = lastNDaysIso(days);
  return dates.map(date => {
    const onDay = all.filter(r => r.date === date && isActiveReservation(r));
    return { date, count: onDay.length, guests: onDay.reduce((s, r) => s + (Number(r.guests) || 0), 0) };
  });
}

/** Bestell-Umsatz-Trend der letzten 7 Tage (Cent-Brutto, ohne stornierte).
 *  S122 — zusätzlich pro-Kanal-Counts (`orderCount` = Walk-In+Takeaway,
 *  `deliveryCount` = Lieferungen) für die Trend-Pills der neuen Spalten.
 *  `count` (gesamt) + `revenueGross` bleiben unverändert (Umsatz-Sparkline).
 *  Test-Hook (S122). */
export function getOrderTrend(days = 7) {
  const state = getState();
  const all = state.orders || [];
  const dates = lastNDaysIso(days);
  return dates.map(date => {
    const onDay = all.filter(o => o.pickupDate === date && o.status !== 'cancelled' && o.status !== 'no_show');
    return {
      date,
      count:         onDay.length,                                              // gesamt (Backward-Compat)
      orderCount:    onDay.filter(o => orderCountChannel(o) === 'order').length,
      deliveryCount: onDay.filter(o => orderCountChannel(o) === 'delivery').length,
      revenueGross:  onDay.reduce((s, o) => s + (Number(o.totalGrossCents) || 0), 0)
    };
  });
}

/** Trend-Pill aus zwei Werten (heute vs. Vortag): {label, dir}. */
export function computeTrend(prev, curr) {
  if (prev === 0 && curr === 0) return { label: '–', dir: 'flat' };
  if (prev === 0) return { label: '+neu', dir: 'up' };
  const delta = ((curr - prev) / prev) * 100;
  const abs = Math.abs(delta);
  const dir = delta > 5 ? 'up' : (delta < -5 ? 'down' : 'flat');
  const sign = delta > 0 ? '+' : (delta < 0 ? '−' : '');
  const label = sign + Math.round(abs) + '%';
  return { label, dir };
}

/**
 * S62 — Live-Counter für die Cockpit-Triade-Kachel.
 * Zählt offene Arbeit pro Operations-Display:
 *   - KDS:       Orders mit kitchenStatus ∈ {pending, preparing} (heute, ohne cancelled/picked_up)
 *   - Theke:     Orders mit barStatus ∈ {pending, preparing} (heute, ohne cancelled/picked_up)
 *   - Lieferung: Orders mit service_type='delivery' UND deliveryStatus ∈ {pending, assigned, picked_up, out_for_delivery}
 *
 * Pre-Order-Window (60 Min) wird hier NICHT angewendet — wir zeigen den Backlog.
 */
export function getCockpitCounters() {
  const state = getState();
  const orders = Array.isArray(state.orders) ? state.orders : [];
  const today = todayISO();
  const isActive = (o) => o && o.status !== 'cancelled' && o.status !== 'picked_up' && o.status !== 'no_show';

  let kds = 0, bar = 0, delivery = 0;
  for (const o of orders) {
    if (!o) continue;
    // KDS + Theke: heute geplante Orders mit offener Sub-Channel-Arbeit. S132b —
    // item-kanal-genau (Single-Source orderChannels/itemsFor, Memory
    // gg-item-single-source): Theke zählt nur Orders mit Getränke-Items, Küche nur
    // mit Speise-Items. Unbekannte Items (Snapshot leer + noch nicht geladen) →
    // {false,false} → Status quo (beide), damit keine offene Arbeit verschwindet;
    // ensureCockpitItemsLoaded() lädt nach + korrigiert die Zähler in place.
    if (o.pickupDate === today && isActive(o)) {
      const k = o.kitchenStatus || 'pending';
      const b = o.barStatus || 'pending';
      const { hasFood, hasDrinks } = orderChannels(itemsFor(o));
      const unknown = !hasFood && !hasDrinks;
      if ((hasFood || unknown) && (k === 'pending' || k === 'preparing')) kds++;
      if ((hasDrinks || unknown) && (b === 'pending' || b === 'preparing')) bar++;
    }
    // Lieferung: aktive delivery-Pipeline, Datum-unabhängig — eine Fahrt heute Abend
    // kann morgen früh delivered werden
    if (o.serviceType === 'delivery' && isActive(o)) {
      const d = o.deliveryStatus || 'pending';
      if (d === 'pending' || d === 'assigned' || d === 'picked_up' || d === 'out_for_delivery') {
        delivery++;
      }
    }
  }
  return { kds, bar, delivery };
}
